import React, { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../../AuthContext";
import axiosInstance from "../../axiosConfig";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import FoamCuttingSharePointLots from "../../components/FoamCuttingSharePointLots";
import "../../assets/CSS/Dashboard.css";
import "../../assets/CSS/SupervisorCuttingLotDetails.css";

const normalizeDepartment = value => String(value || "")
  .trim()
  .replace(/\s+/g, " ")
  .toUpperCase()
  .replace(/^OPERATION\s*:\s*/, "")
  .replace(/^[\s\-–—:]+|[\s\-–—:]+$/g, "")
  .replace(/\bCUTING\b/g, "CUTTING")
  .replace(/\s+(?:DEPT|DEPARTMENT)$/, "")
  .replace(/^.*\b(?:PRESSING|GLOWING|GLUING)\b.*$/, "PRESSING")
  .trim();

const formatDepartment = value => {
  const department = normalizeDepartment(value);
  if (department === "PRESSING") return "PRESSING / GLOWING";
  return department
    .toLowerCase()
    .replace(/\b\w/g, character => character.toUpperCase());
};

export default function SupervisorCuttingLotDetails() {
  const { user } = useContext(AuthContext);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const requestedDepartment = useMemo(() => normalizeDepartment(
    new URLSearchParams(window.location.search).get("department")
  ), []);

  useEffect(() => {
    let active = true;
    const loadDepartments = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axiosInstance.get("/api/foam-cutting-sharepoint/lots", {
          params: { allDepartments: 1 },
        });
        const visible = [...new Set((response.data?.departments || [])
          .map(item => normalizeDepartment(item?.department || item))
          .filter(Boolean))];
        if (!active) return;
        setDepartments(visible);
        setSelectedDepartment(visible.includes(requestedDepartment) ? requestedDepartment : (visible[0] || ""));
      } catch (requestError) {
        if (active) setError(requestError.response?.data?.message || "Assigned cutting departments load nahi ho paye.");
      } finally {
        if (active) setLoading(false);
      }
    };
    loadDepartments();
    return () => { active = false; };
  }, [requestedDepartment]);

  return (
    <div className="d-flex dashboard supervisor-cutting-layout">
      <div className={isSidebarVisible ? "sidebar-container shadow-sm" : "sidebar-hidden"}>
        <Sidebar />
      </div>
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <Header toggleSidebar={() => setIsSidebarVisible(previous => !previous)} isSidebarVisible={isSidebarVisible} />
        <main className="main-container supervisor-cutting-main">
          <div className="supervisor-cutting-content">
            <section className="supervisor-cutting-hero">
              <div className="supervisor-cutting-hero-copy">
                <div className="supervisor-cutting-eyebrow"><i className="bi bi-broadcast-pin" /> Live Production Control</div>
                <div className="d-flex align-items-center gap-3 mt-3">
                  <span className="supervisor-cutting-hero-icon"><i className="bi bi-scissors" /></span>
                  <div>
                    <h1>Cutting Lots</h1>
                    <p>SharePoint workbook ka latest department-wise production data.</p>
                  </div>
                </div>
              </div>

              {!loading && departments.length > 0 && <div className="supervisor-department-switcher">
                <span className="supervisor-switcher-label">Viewing Department</span>
                <div className="supervisor-switcher-buttons" role="group" aria-label="Select cutting department">
                  {departments.map(department => <button type="button" key={department} className={selectedDepartment === department ? "active" : ""} onClick={() => setSelectedDepartment(department)}>
                    <i className="bi bi-building-check" />
                    <span>{formatDepartment(department)}</span>
                    {selectedDepartment === department && <i className="bi bi-check-circle-fill" />}
                  </button>)}
                </div>
              </div>}
            </section>

            {!loading && selectedDepartment && <section className="supervisor-cutting-stats" aria-label="Cutting lot access summary">
              <div className="supervisor-cutting-stat">
                <span className="stat-icon stat-icon-blue"><i className="bi bi-person-badge" /></span>
                <div><small>Supervisor</small><strong>{user?.Name || "-"}</strong></div>
              </div>
              <div className="supervisor-cutting-stat">
                <span className="stat-icon stat-icon-green"><i className="bi bi-diagram-3" /></span>
                <div><small>Assigned Access</small><strong>{departments.length} Department{departments.length === 1 ? "" : "s"}</strong></div>
              </div>
              <div className="supervisor-cutting-stat">
                <span className="stat-icon stat-icon-amber"><i className="bi bi-lightning-charge-fill" /></span>
                <div><small>Data Source</small><strong><span className="live-dot" />Live SharePoint</strong></div>
              </div>
            </section>}

            <section className="supervisor-cutting-data">
              {loading ? <div className="supervisor-cutting-state"><div className="spinner-border text-success" role="status" /><strong>Departments load ho rahe hain...</strong><span>Supervisor assignment check ki ja rahi hai.</span></div>
                : error ? <div className="alert alert-danger shadow-sm"><i className="bi bi-exclamation-triangle-fill me-2" />{error}</div>
                  : selectedDepartment ? <FoamCuttingSharePointLots key={selectedDepartment} department={selectedDepartment} />
                    : <div className="supervisor-cutting-state warning"><i className="bi bi-person-exclamation" /><strong>Workbook department assigned nahi hai</strong><span>Supervisor Departments mein required department mapping add karein.</span></div>}
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}
